import type { BadgeStatus } from '@indipia/ui-core';

/**
 * 공정별 반장 일정 확정 여부 표시 (REQ-11-01 · 02, 설계서 8.1 상태 태그 문구).
 * 작업요청 작성(004 B)·일괄 작업요청(004 B-4)이 같은 문구·색을 쓰도록 한 곳에 둔다.
 */
export interface PartnerStatusView {
  /** 태그 문구 */
  label: string;
  /** Badge status (D-09 §15.5) — 색만으로 뜻을 전하지 않도록 라벨과 함께 쓴다 */
  status: Extract<BadgeStatus, 'success' | 'warning' | 'danger' | 'mute' | 'info'>;
  /** 반장 일정이 확정된 공정 — 목록 맨 위, 흐림 없음 */
  confirmed: boolean;
}

/** 작업요청 작성(site-info 공정)과 일괄 작업요청(후보 공정)이 필드 이름만 달라서(partnerId ↔ assigneePartnerId) 호출부가 맞춰 넘긴다. */
export interface PartnerStatusSource {
  /** 담당 시공반장 — 없으면 상태와 무관하게 '미배정' */
  partnerId: number | null;
  partnerStatus: string | null;
  replyExpireDtm: string | null;
  /** SIF-1 '자재파트너가 시공' 공종 — 자재거래처가 시공까지 맡으므로 미배정이 아니라 '자재파트너' */
  constructByVendor?: boolean | null;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'MM.DD HH:mm' — 기한 표기. 서버가 시간대 없는 일시(KST)를 주므로 브라우저 로컬 시각으로 읽는다. 읽을 수 없으면 null. */
export function fmtMonthDayTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * 반장 상태 → 태그. 스케줄 미연동 등으로 상태 값이 없으면 null(태그 · 정렬 · 경고 모두 하지 않는다).
 *
 * | partnerStatus | 태그 | 색 |
 * |---|---|---|
 * | (자재파트너가 시공 공종 — constructByVendor) | 자재파트너 | info (확정과 같이 취급) |
 * | CONFIRMED | 일정가능 | success |
 * | WAITING | 회신 대기 · 기한 MM.DD HH:mm (기한 없으면 '회신 대기') | warning |
 * | ASSIGNED | 일정확인 미발송 | warning |
 * | DECLINED · UNFIXED (그 밖의 값 포함) | 반장님 미확정 | danger |
 * | UNASSIGNED · UNASSIGNABLE, 또는 담당 반장 없음 | 미배정 | mute |
 */
export function partnerStatusView(wt: PartnerStatusSource): PartnerStatusView | null {
  if (wt.partnerStatus == null) return null;
  if (wt.constructByVendor === true) return { label: '자재파트너', status: 'info', confirmed: true };
  if (wt.partnerId == null) return { label: '미배정', status: 'mute', confirmed: false };
  switch (wt.partnerStatus) {
    case 'CONFIRMED':
      return { label: '일정가능', status: 'success', confirmed: true };
    case 'WAITING': {
      const due = fmtMonthDayTime(wt.replyExpireDtm);
      return { label: due ? `회신 대기 · 기한 ${due}` : '회신 대기', status: 'warning', confirmed: false };
    }
    case 'ASSIGNED':
      return { label: '일정확인 미발송', status: 'warning', confirmed: false };
    case 'UNASSIGNED':
    case 'UNASSIGNABLE':
      return { label: '미배정', status: 'mute', confirmed: false };
    default:
      return { label: '반장님 미확정', status: 'danger', confirmed: false };
  }
}

/**
 * 인디플래너 간트의 시공파트너배정 · 알림발송 팝업을 그 공정에 맞춰 여는 주소 (X-04 / X-06).
 * scheduler 에이전트가 읽는 쿼리 — view=gantt · siteId · taskId · pop=pt-assign. 에이전트끼리 import 하지 않고 주소만 공유한다.
 */
export function ganttPartnerAssignPath(siteId: number, taskId: number): string {
  const q = new URLSearchParams({ view: 'gantt', siteId: String(siteId), taskId: String(taskId), pop: 'pt-assign' });
  return `/agents/scheduler?${q.toString()}`;
}
